// @flow
/* eslint-disable max-lines, max-lines-per-function */
import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import type { ContextRouter } from 'react-router'
import moment from 'moment'

import isEmpty from "lodash/isEmpty"
import Strings, { Routes, DISPLAY_DATE_FORMAT } from '@/constants'
import selectors from '@/selectors'
import { base64ToObject, getImageUrl, objectToBase64, dataFromQueryString } from '@/helpers'
import { ErrorBar, ModalLoading, Carousel, SearchBar, Meta } from '@/components'
import TopBar from '@/components/top-bar'
import { Property } from '@/models'
import { ActionCreators } from '@/redux'

import { PropertyInfo } from './subcomponents'

import styles from './styles.scss'


type PassedProps = {|
  ...$Exact<ContextRouter>,
|}

type StateToProps = {|
  search: string,
  locations: {
    values: Array<Location>,
    isPending: boolean,
    errors: ?ResponseError
  },
  properties: {
    values: Array<Property>,
    isPending: boolean,
    errors: ?ResponseError
  }
|}

type DispatchToProps = {|
  fetchProperty: (data: SearchPayload) => void,
|}

type HotelInfoProps = {|
  ...PassedProps,
  ...StateToProps,
  ...DispatchToProps,
|}

export const HotelInfoScreen = (props: HotelInfoProps) => {
  const { properties, match, search, history, fetchProperty } = props
  const { code } = match.params
  const [property, setProperty] = useState<Property | null>(null)
  const [images, setImages] = useState<Array<string>>([])
  const [firstLoad, setFirstLoad] = useState<boolean>(true)

  const getSearchDataOrDefault = () => {
    const { search: query } = dataFromQueryString(search)
    const {
      fromDate,
      toDate,
      destination,
      adults,
      children,
      tags,
    } = base64ToObject(query)

    const isFromDateValid = !fromDate || moment(fromDate, DISPLAY_DATE_FORMAT).isAfter(moment().add(7, 'd'))
    const isToDateValid = !toDate || (
      moment(toDate, DISPLAY_DATE_FORMAT).isAfter(moment(fromDate, DISPLAY_DATE_FORMAT).add(2, 'd'))
      && moment(toDate, DISPLAY_DATE_FORMAT).isBefore(moment(fromDate, DISPLAY_DATE_FORMAT).add(28, 'd'))
    )

    const data = {
      fromDate: (isFromDateValid && fromDate) || moment().add(1, 'M').format(DISPLAY_DATE_FORMAT),
      toDate: (isToDateValid && toDate) || moment().add(1, 'M').add(3, 'd').format(DISPLAY_DATE_FORMAT),
      // $FlowFixMe
      destination: !isEmpty(destination) && destination || (property?.region ? [`${property?.region}`] : []),
      adults: adults || 2,
      children: children || [],
      tags: tags || [],
    }

    if (!isFromDateValid || !isToDateValid) {
      history.push({
        // $FlowFixMe
        pathname: Routes.HotelInfo.replace(':code', code),
      })
      return [data, false]
    }

    return [data, true]
  }

  const goBack = () => {
    const [data] = getSearchDataOrDefault()

    history.push({
      pathname: Routes.SearchResults,
      search: `search=${objectToBase64({...data, propertyId: code})}`
    })
  }

  useEffect(() => {
    if (!code) goBack()

    const [currentProperty] = properties.values.filter(p => p.code === code)
    
    if ((firstLoad || !currentProperty) && !properties.isPending && !properties.errors) {
      const [data, isValid] = getSearchDataOrDefault()
      const fromDate = moment(data.fromDate.replace(/\s+/g,""))
      const toDate = moment(data.toDate.replace(/\s+/g,""))
      if(fromDate.isAfter(toDate)) { 
        history.replace({
          // $FlowFixMe
          pathname: Routes.HotelInfo.replace(':code', code),
        })
      }
      setFirstLoad(false)
      if (isValid) {
        fetchProperty({ ...data, code })
      }
    } else {
      setProperty(currentProperty)
    }

  }, [code, properties, firstLoad])

  useEffect(() => {
    if (!property) return
    const { images: propertyImages } = property.metadata
    // $FlowFixMe
    setImages(propertyImages.map(image => getImageUrl(image)).filter(url => url))
  }, [property])

  const chooseRoom = (roomId: string, rateId: string, checkLink: string) => {
    const [data] = getSearchDataOrDefault()

    history.push({
      pathname: Routes.Checkout,
      search: `search=${objectToBase64({...data, propertyId: code, roomId, rateId, checkLink})}`
    })
  }

  const errorMessage = useMemo(() => {
    try {
      if (properties.errors) {
        setTimeout(() => {
          window.location.replace('.')
        }, 3000)
        // $FlowFixMe
        const errorDetail = properties.errors?.response?.data?.error?.detail
        if (errorDetail) {
          const errorMessage = JSON.parse(errorDetail)?.errors[0]?.message
          return errorMessage
        }

        return Strings.general.somethingWentWrong
      }
    } catch (e) {
      console.warn(e)
      return Strings.general.somethingWentWrong
    }
    return undefined
  }, [properties.errors])

  return (
    <>
      <Meta title={property?.metadata?.name || ''} description={property?.metadata?.descriptions?.headline} />
      <div className={styles.topBar}>
        <TopBar
          goBack={goBack}
          title={Strings.general.backToHotel}
          match={match}
        />
      </div>
      <Carousel images={images} />
      <div className={styles.background}>
        <div className={styles.updateContainerPadding}>
          <SearchBar
            className={styles.searchBar}
            style={{
              position: 'relative',
              top: `calc(-${styles.bigButtonHeight} / 2)`,
            }}
            match={match}
          />
        </div>
        <ModalLoading visible={properties.isPending} />
        {
          errorMessage && (
            <div className={styles.topError}>
              {/* $FlowFixMe */}
              <ErrorBar error={errorMessage} />
            </div>
          )
        }
        { property && <PropertyInfo property={property} chooseRoom={chooseRoom} goBack={goBack} /> }
      </div>
    </>
  )
}

const mapStateToProps = (state: StoreState): StateToProps => ({
  // $FlowFixMe
  search: state.router.location.search || '',
  properties: selectors.apple.getProperties(state),
  locations: selectors.apple.getLocations(state)
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchToProps => ({
  fetchProperty: (data: SearchPayload) =>
    dispatch(ActionCreators.apple.searchPropertiesRequest.dispatch(data))
})

// $FlowFixMe
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HotelInfoScreen)
